import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { connect } from 'react-redux';
import { setHelmetTags } from 'helpers/HelmetHelper';
import { noop } from 'helpers/GlobalHelper';
import { helperGoToBioLanding } from 'helpers/RouteHelper';
import Loader from 'components/Loader';
import { getCreditMemberUrl, getCreditMemberId, getGroupedCast } from './BioHelpers';
import { fetchBioLanding, fetchBioDetail, clearBioPages } from './BioActions';
import CastItemComponent from './CastItem';
import BioDetailComponent from './BioDetail';
import Style from './Bio.module.scss';

class Bio extends Component {
  constructor(props) {
    super(props);

    this.getPrevious = this.getPrevious.bind(this);
    this.getNext = this.getNext.bind(this);
  }

  componentDidMount() {
    const {
      showId,
      cast,
      itemId,
      accessToken,
      deviceId,
      fetchBioDetail,
      fetchBioLanding,
    } = this.props;
    if (itemId) return fetchBioDetail(itemId, accessToken, deviceId);
    if (!cast) return fetchBioLanding(showId, accessToken, deviceId);
    return null;
  }

  UNSAFE_componentWillUpdate(nextProps) {
    const { showId, cast, itemId, fetchBioDetail, fetchBioLanding } = this.props;
    const {
      accessToken,
      deviceId,
      itemId: nextItemId,
      showId: nextShowId,
      errors: nextErrors,
    } = nextProps;

    if (nextItemId && nextErrors === 404) return helperGoToBioLanding(showId);

    const hasProfile = this.hasProfile(nextItemId);
    const shouldFetchItem = !hasProfile && nextItemId && itemId !== nextItemId;
    const shouldFetchCast = !cast || showId !== nextShowId;

    if (shouldFetchItem) return fetchBioDetail(nextItemId, accessToken, deviceId);
    if (shouldFetchCast) return fetchBioLanding(nextShowId, accessToken, deviceId);
    return null;
  }

  componentWillUnmount() {
    const { clearBioPages } = this.props;
    clearBioPages();
  }

  getCastWithImages = cast => _.filter(cast, castItem => castItem.images);

  getCurrentCastIndex() {
    const { cast, itemId } = this.props;
    const castWithImages = this.getCastWithImages(cast);
    if (!cast || !itemId) return null;
    return _.findIndex(castWithImages, member => getCreditMemberId({ member }) === itemId);
  }

  getNext() {
    const { cast } = this.props;
    const castWithImages = this.getCastWithImages(cast);
    const currentIndex = this.getCurrentCastIndex();
    return _.get(castWithImages, currentIndex + 1);
  }

  getPrevious() {
    const { cast } = this.props;
    const castWithImages = this.getCastWithImages(cast);
    const currentIndex = this.getCurrentCastIndex();
    return _.get(castWithImages, currentIndex - 1);
  }

  hasProfile(id) {
    const { profiles } = this.props;
    return _.get(profiles, id, false);
  }

  renderBio() {
    const { landingSeo, cast } = this.props;
    const orderedCast = getGroupedCast(cast);
    return (
      <div className={Style.container} data-test="web-bio">
        {setHelmetTags(landingSeo)}
        {orderedCast &&
          _.map(orderedCast, (castList, category) => (
            <div key={`bio-cast-list-${category}`}>
              <h1 className={Style.title} data-test="web-bio-title">
                {category}
              </h1>
              {this.renderCast(castList)}
            </div>
          ))}
      </div>
    );
  }

  renderBioDetail() {
    const { breakpoint, profiles, showId, itemId } = this.props;
    const featured = _.get(profiles, itemId);
    const seo = _.get(featured, 'seo');
    const item = _.get(featured, 'featured');
    const related = _.get(featured, 'related');

    const props = {
      breakpoint,
      getNext: this.getNext,
      getPrevious: this.getPrevious,
      showId,
      item,
      related,
      seo,
    };
    return <BioDetailComponent {...props} />;
  }

  renderCast(cast) {
    const { breakpoint, selectMenuItemEvent, showId } = this.props;

    if (!cast) return null;
    const castItems = cast.map(member => {
      if (!member || !member.images) return null;
      const url = getCreditMemberUrl({ showId, member });
      const props = {
        selectMenuItemEvent,
        breakpoint,
        key: member.id,
        data: member,
        url,
      };
      return <CastItemComponent {...props} />;
    });

    return (
      <div className={Style.cast} data-test="web-bio-cast">
        {castItems}
      </div>
    );
  }

  render() {
    const { cast, itemId, setLoading } = this.props;
    const hasProfile = this.hasProfile(itemId);
    const hasCast = cast && cast.length > 0;
    const showLoader = (itemId && !hasProfile) || !cast;
    const loader = <Loader setLoading={setLoading} center />;

    if (showLoader) return loader;
    if (hasProfile) return this.renderBioDetail();
    if (hasCast) return this.renderBio();
    return null;
  }
}

Bio.defaultProps = {
  accessToken: null,
  breakpoint: null,
  cast: null,
  clearBioPages: noop,
  deviceId: null,
  errors: null,
  fetchBioDetail: noop,
  fetchBioLanding: noop,
  itemId: null,
  landingSeo: null,
  profiles: [],
  selectMenuItemEvent: noop,
  seo: null,
  setLoading: noop,
  setErrors: noop,
  showId: null,
};

Bio.propTypes = {
  accessToken: PropTypes.string,
  breakpoint: PropTypes.string,
  cast: PropTypes.arrayOf(PropTypes.shape({})),
  clearBioPages: PropTypes.func,
  deviceId: PropTypes.string,
  errors: PropTypes.number,
  fetchBioDetail: PropTypes.func,
  fetchBioLanding: PropTypes.func,
  itemId: PropTypes.string,
  landingSeo: PropTypes.shape({}),
  profiles: PropTypes.shape({}),
  selectMenuItemEvent: PropTypes.func,
  seo: PropTypes.shape({}),
  setLoading: PropTypes.func,
  setErrors: PropTypes.func,
  showId: PropTypes.string,
};

const mapStateToProps = state => ({
  errors: _.get(state, 'bio.errors'),
  profiles: _.get(state, 'bio.profiles'),
  seo: _.get(state, 'bio.seo'),
  landingSeo: _.get(state, 'bio.landingSeo'),
  cast: _.get(state, 'bio.cast'),
});

export default connect(
  mapStateToProps,
  { fetchBioLanding, fetchBioDetail, clearBioPages },
)(Bio);
